import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { T, F, Over, Pill, Btn } from "@/lib/bricks";

// Tier A + commercial (builder/developer) jobs need a verified, unexpired
// certificate of insurance with drone liability >= $2M (job can require more,
// e.g. $5M under CCDC 41). Flags never demote or notify the vendor.
export const INSURANCE_REASON = {
  insurance_not_verified: "No verified certificate of insurance",
  insurance_no_drone_liability: "COI does not cover drone liability",
  insurance_no_expiry: "COI expiry not recorded",
  insurance_expired: "COI expired",
  insurance_below_minimum: "Liability below $2,000,000",
  company_insurance_missing: "Company policy not recorded",
  company_insurance_not_verified: "Company policy not verified",
  company_insurance_no_drone_liability: "Company policy lacks drone liability",
  company_insurance_no_expiry: "Company policy expiry not recorded",
  company_insurance_expired: "Company policy expired",
  company_insurance_below_minimum: "Company policy below $2,000,000",
  company_policy_excludes_employees: "Company policy does not cover employees",
};

export function TierInsuranceFlag({ v }) {
  if (!v?.top_tier_insurance_missing) return null;
  return (
    <Pill tone="warn" data-testid="tier-a-insurance-flag">
      Tier A · {INSURANCE_REASON[v.top_tier_insurance_missing] || "insurance missing"}
    </Pill>
  );
}

export default function InsuranceSection({ v, p, onSaved }) {
  const ins = v.insurance || {};
  const coiDocs = (v.documents || []).filter((d) => d.doc_type === "coi");
  const latest = coiDocs[0];
  const declared = latest?.declared || {};
  const [limit, setLimit] = useState("");
  const [expires, setExpires] = useState("");
  const [aviation, setAviation] = useState(false);
  const [policy, setPolicy] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLimit(String(ins.liability_cad || declared.liability_cad || ""));
    setExpires(ins.expires_on || declared.expires_on || latest?.expiry_date || "");
    setAviation(Boolean(ins.aviation_covered || declared.aviation_covered));
    setPolicy(ins.policy_number || declared.policy_number || "");
    // eslint-disable-next-line
  }, [v.id, latest?.id]);

  const save = async (verified) => {
    setBusy(true);
    try {
      await api.put(`/admin/vendors/${v.id}/insurance`, {
        verified,
        liability_cad: Number(limit) || null,
        expires_on: expires || null,
        aviation_covered: aviation,
        policy_number: policy,
        document_id: latest?.id || null,
      });
      toast.success(verified ? "Insurance verified" : "Insurance marked unverified");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save insurance");
    } finally {
      setBusy(false);
    }
  };

  const input = { fontFamily: F.display, fontSize: 13, padding: "7px 10px", border: `1px solid ${p.hair}`, borderRadius: 2, background: "transparent", color: p.ink };

  // In-house staff are covered by the org-level company policy, not a personal COI.
  if (v.worker_type === "employee") {
    return (
      <section data-testid="applicant-insurance" style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0, color: p.ink }}>Insurance</h2>
          <Pill tone={ins.valid ? "ok" : "warn"}>
            {ins.valid ? `Covered by company policy to ${ins.expires_on}` : INSURANCE_REASON[ins.reason] || "Company policy not verified"}
          </Pill>
        </div>
        <div style={{ fontSize: 13, color: p.ink3 }}>
          Covered by the company policy recorded once at org level (no personal COI needed). Salaried, so no per-job payout.
          {v.commercial_eligible ? " Can take commercial jobs." : ""}
        </div>
      </section>
    );
  }

  return (
    <section data-testid="applicant-insurance" style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0, color: p.ink }}>Insurance</h2>
        <Pill tone={ins.valid ? "ok" : "warn"}>
          {ins.valid ? `Valid to ${ins.expires_on}` : INSURANCE_REASON[ins.reason] || "Not verified"}
        </Pill>
      </div>
      <div style={{ fontSize: 13, color: p.ink3, marginBottom: 12 }}>
        Builder and developer jobs, and Tier A, need a verified certificate of insurance with drone (aviation) liability of at least $2,000,000, unexpired on the job date. Listing jobs do not read this.
        {v.commercial_eligible ? " This vendor can take commercial jobs." : ""}
      </div>
      {latest ? (
        <div style={{ fontSize: 13, color: p.ink3, marginBottom: 12 }}>
          Latest certificate: {String(latest.status || "").replace(/_/g, " ")} ·{" "}
          {latest.signed_url ? <a href={latest.signed_url} target="_blank" rel="noreferrer" style={{ color: p.ink }}>open file</a> : "file on record"}
          {declared.insurer ? ` · ${declared.insurer}` : ""}
          {declared.liability_cad ? ` · declared $${Number(declared.liability_cad).toLocaleString("en-CA")}` : ""}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: p.ink4, marginBottom: 12 }}>No certificate uploaded. Vendors upload it from their profile in the photographer app.</div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input aria-label="Liability limit (CAD)" placeholder="Liability (CAD)" value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^0-9]/g, ""))} style={{ ...input, width: 190 }} />
        <input aria-label="Expires on" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} style={input} />
        <input aria-label="Policy number" placeholder="Policy number" value={policy} onChange={(e) => setPolicy(e.target.value)} style={{ ...input, width: 140 }} />
        <label style={{ fontSize: 12, color: p.ink3, display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={aviation} onChange={(e) => setAviation(e.target.checked)} /> Covers drone liability
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <Btn disabled={busy || !limit || !expires} onClick={() => save(true)} style={{ padding: "7px 14px", fontSize: 12 }}>{busy ? "…" : "Verify insurance"}</Btn>
        {ins.verified ? (
          <Btn variant="ghost" disabled={busy} onClick={() => save(false)} style={{ padding: "7px 14px", fontSize: 12 }}>Mark unverified</Btn>
        ) : null}
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: p.ink4 }}>Only you see this. No email or push is sent.</div>
    </section>
  );
}
